import { html, css } from 'lit';

import { BUTTON_ROLE } from '../../../packages/neb-lit-components/src/components/neb-button';
import { POPOVER_POSITION } from '../../../packages/neb-lit-components/src/components/neb-popover';
import { parseDate } from '../../../packages/neb-utils/date-util';
import Debouncer from '../../../packages/neb-utils/debouncer';
import { uniq } from '../../../packages/neb-utils/utils';
import { CSS_BORDER_GREY_2, CSS_SPACING } from '../../styles';
import { DISPLAY_MODES } from '../controls/inputs/neb-select-search-multi';

import '../../../packages/neb-lit-components/src/components/inputs/neb-select';
import '../../../packages/neb-lit-components/src/components/inputs/neb-textfield';
import '../../../packages/neb-lit-components/src/components/inputs/neb-floating-label';
import '../../../packages/neb-lit-components/src/components/neb-date-picker';
import '../controls/field-groups/neb-range-date';
import { NebFilters, ELEMENTS as BASE_ELEMENTS } from './neb-filters';

export const ELEMENTS = {
  ...BASE_ELEMENTS,
  providersSelect: { id: 'providers-select' },
  casesSelect: { id: 'cases-select' },
  dosLabel: { id: 'dos-label' },
  dosFrom: { id: 'dos-from' },
  dosTo: { id: 'dos-to' },
};

class NebFiltersProgressionOfCare extends NebFilters {
  static get properties() {
    return {
      search: Object,
      items: Object,
      appliedFilters: Object,
      filteredItems: Object,
    };
  }

  static get styles() {
    return [
      super.styles,
      css`
        .container {
          border: 0;
          border-bottom: ${CSS_BORDER_GREY_2};
        }

        .content {
          display: flex;
          flex-wrap: wrap;
          padding-bottom: ${CSS_SPACING};
        }

        neb-select-search-multi {
          height: 55px;
          flex: 1;
        }

        neb-date-picker {
          width: 100%;
          flex: 1;
        }

        .floating-label {
          left: 0;
          padding: 0 4px;
        }

        .date-range {
          display: flex;
          gap: ${CSS_SPACING};
          position: relative;
          width: 100%;
        }
      `,
    ];
  }

  static createModel() {
    return {};
  }

  initState() {
    super.initState();

    this.search = {
      providers: '',
      cases: '',
    };

    this.items = {
      providers: [],
      cases: [],
    };

    this.filteredItems = {
      providers: [],
      cases: [],
    };

    this.appliedFilters = {
      providers: [],
      cases: [],
      dosFrom: null,
      dosTo: null,
    };

    this.onReset = () => {};

    this.onChange = () => {};

    this.onUpdateAppliedFilters = () => {};

    this.__applyDebouncer = new Debouncer(() => {
      this.onApply(this.__pendingAppliedFilters);
    });
  }

  __hasAppliedFilters(filters) {
    const arrayKeys = Object.keys(filters).filter(key =>
      Array.isArray(filters[key]),
    );

    if (arrayKeys.some(key => filters[key].length > 0)) {
      return true;
    }

    const nonArrayKeys = Object.keys(filters).filter(
      key => !Array.isArray(filters[key]),
    );

    if (
      nonArrayKeys.some(
        key =>
          filters[key] !== null &&
          filters[key] !== undefined &&
          filters[key] !== '',
      )
    ) {
      return true;
    }

    return false;
  }

  initHandlers() {
    super.initHandlers();

    this.handlers = {
      ...this.handlers,
      search: ({ name, value }) => {
        this.search = {
          ...this.search,
          [name]: value,
        };

        const items = this.items[name];
        const filteredItems = items.filter(item =>
          item.label
            .toLowerCase()
            .replace(/[\s-]/g, '')
            .includes(value.toLowerCase().replace(/[\s-]/g, '')),
        );

        this.filteredItems = {
          ...this.filteredItems,
          [name]: filteredItems,
        };
      },
      selectMultiSearch: ({ name, value }) => {
        const currentIds = this.appliedFilters[name];

        const mapIds = items => items.map(item => item.value.id);
        const idsToRemove = mapIds(value.filter(item => !item.checked));
        const idsToAdd = mapIds(value.filter(item => item.checked));

        const ids = [
          ...currentIds.filter(id => !idsToRemove.includes(id)),
          ...idsToAdd,
        ];

        this.appliedFilters = {
          ...this.appliedFilters,
          [name]: uniq(ids),
        };

        this.onChange(this.appliedFilters);
      },
      changeDate: ({ name, value }) => {
        const getTime = name === 'dosFrom' ? 'startOf' : 'endOf';
        const newDate = value ? parseDate(value)[getTime]('day') : null;

        this.appliedFilters = {
          ...this.appliedFilters,
          [name]: newDate ? newDate.toISOString() : null,
        };

        if (name === 'dosFrom') {
          this.__updateDosFromSelectable();
        } else {
          this.__updateDosToSelectable();
        }
        this.onChange(this.appliedFilters);
      },
      apply: () => {
        this.__pendingAppliedFilters = this.appliedFilters;
        this.filteredItems = { ...this.items };
        this.search = Object.keys(this.search).reduce(
          (acc, key) => ({
            ...acc,
            [key]: '',
          }),
          {},
        );

        if (this.__hasAppliedFilters(this.__pendingAppliedFilters)) {
          this.__applyDebouncer.debounce();
        }
      },
      reset: () => {
        this.appliedFilters = {
          providers: [],
          cases: [],
          dosFrom: null,
          dosTo: null,
        };

        this.onReset();
      },
    };

    this.__updateDosFromSelectable();
    this.__updateDosToSelectable();
  }

  __updateDosFromSelectable() {
    this.handlers.isDosFromSelectable = date =>
      (!this.appliedFilters.dosTo ||
        date.isSameOrBefore(parseDate(this.appliedFilters.dosTo))) &&
      date.isSameOrBefore(parseDate().startOf('day'));
  }

  __updateDosToSelectable() {
    this.handlers.isDosToSelectable = date =>
      !this.appliedFilters.dosFrom ||
      date.isSameOrAfter(parseDate(this.appliedFilters.dosFrom));
  }

  updated(changedProps) {
    super.updated(changedProps);

    if (changedProps.has('items')) {
      this.filteredItems = this.items;
    }

    if (changedProps.has('appliedFilters')) {
      this.onUpdateAppliedFilters(this.appliedFilters);
    }
  }

  __getDosToValue() {
    return this.appliedFilters?.dosTo
      ? parseDate(this.appliedFilters.dosTo)
      : null;
  }

  __getDosFromValue() {
    return this.appliedFilters?.dosFrom
      ? parseDate(this.appliedFilters.dosFrom)
      : null;
  }

  __renderProviderField() {
    return html`
      <neb-select-search-multi
        id="${ELEMENTS.providersSelect.id}"
        name="providers"
        label="Providers"
        .search="${this.search.providers}"
        .displayMode="${DISPLAY_MODES.MENU}"
        .items="${this.filteredItems.providers}"
        .totalItems="${this.items.providers.length}"
        .onSearch="${this.handlers.search}"
        .onChange="${this.handlers.selectMultiSearch}"
      ></neb-select-search-multi>
    `;
  }

  __renderCaseField() {
    return html`
      <neb-select-search-multi
        id="${ELEMENTS.casesSelect.id}"
        name="cases"
        label="Cases"
        .search="${this.search.cases}"
        .displayMode="${DISPLAY_MODES.MENU}"
        .items="${this.filteredItems.cases}"
        .totalItems="${this.items.cases.length}"
        .onSearch="${this.handlers.search}"
        .onChange="${this.handlers.selectMultiSearch}"
      ></neb-select-search-multi>
    `;
  }

  __renderDateFields() {
    return html`
      <div class="date-range">
        <neb-floating-label
          id="${ELEMENTS.dosLabel.id}"
          class="floating-label"
          text="Date Range"
          ?pinned="${true}"
        >
        </neb-floating-label>
        <neb-date-picker
          id="${ELEMENTS.dosFrom.id}"
          name="dosFrom"
          label=" "
          placeholder="From"
          .manualPopoverPosition="${POPOVER_POSITION.CENTER}"
          .selectedDate="${this.__getDosFromValue()}"
          .onChange="${this.handlers.changeDate}"
          .isDateSelectable="${this.handlers.isDosFromSelectable}"
          momentFlag
        ></neb-date-picker>

        <neb-date-picker
          id="${ELEMENTS.dosTo.id}"
          name="dosTo"
          label=" "
          placeholder="To"
          .manualPopoverPosition="${POPOVER_POSITION.CENTER}"
          .selectedDate="${this.__getDosToValue()}"
          .onChange="${this.handlers.changeDate}"
          .isDateSelectable="${this.handlers.isDosToSelectable}"
          momentFlag
        ></neb-date-picker>
      </div>
    `;
  }

  __renderButtons() {
    return html`
      <div class="container-buttons">
        <neb-button
          id="${ELEMENTS.applyButton.id}"
          label="Apply"
          .role="${BUTTON_ROLE.CONFIRM}"
          .onClick="${this.handlers.apply}"
        ></neb-button>
        <neb-button
          id="${ELEMENTS.resetButton.id}"
          label="Reset"
          .role="${BUTTON_ROLE.OUTLINE}"
          .onClick="${this.handlers.reset}"
        ></neb-button>
      </div>
    `;
  }

  renderContent() {
    return html`
      ${this.__renderDateFields()} ${this.__renderProviderField()}
      ${this.__renderCaseField()}
    `;
  }
}

window.customElements.define(
  'neb-filters-progression-of-care',
  NebFiltersProgressionOfCare,
);
